import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import EngineeringIcon from "@mui/icons-material/Engineering";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Box, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { LoadingCard } from "../../common/LoadingCard";
import { WidgetBenefitBlockedState, getBenefitErrorMessage, isBenefitForbiddenError } from "../../common/WidgetBenefitBlockedState";
import { colors } from "../../ui/colors";
import { EWorkforceRole } from "./enums";
import { useGetTotalWorkforce } from "./hooks/useGetTotalWorkforce";

type RoleDetails = {
  label: string;
  color: string;
  icon?: React.ReactNode;
};

type WorkforceData = Record<string, number>;

const ROLES = Object.values(EWorkforceRole);

const ROLE_DETAILS: Record<EWorkforceRole, RoleDetails> = {
  [EWorkforceRole.EMPLOYEE]: { label: "Employees", color: colors.blue.base, icon: <PersonIcon /> },
  [EWorkforceRole.CONTRACTOR]: { label: "Contractors", color: colors.orange.base, icon: <EngineeringIcon /> },
  [EWorkforceRole.SUPERVISOR]: { label: "Supervisors", color: colors.success.base, icon: <StarIcon /> },
  [EWorkforceRole.VIRTUAL_RECEPTIONIST]: { label: "Virtual Receptionists", color: colors.info.base, icon: <SupportAgentIcon /> },
  [EWorkforceRole.ACCOUNT_MANAGER]: { label: "Account Managers", color: colors.danger.base, icon: <BusinessCenterIcon /> },
};

const getRoleCount = (data: WorkforceData, role: EWorkforceRole): number => Number(data?.[role] ?? 0);

type RoleBarProps = {
  role: EWorkforceRole;
  count: number;
  minutes: number;
  widthPercent: number;
};

const RoleBar: React.FC<RoleBarProps> = ({ role, count, minutes, widthPercent }) => {
  const details = ROLE_DETAILS[role] || { label: role, color: colors.gray.base, icon: null };
  const { label, color, icon } = details;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        cursor: "pointer",
        "&:hover .icon, &:hover .label, &:hover .count": { color: colors.gray[600] },
        "&:hover .bar": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "nowrap",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flexShrink: 1 }}>
          <Typography
            className="icon"
            sx={{
              display: "flex",
              color: colors.gray[400],
              "& svg": { fontSize: { md: "0.9rem", lg: "1.1rem" } },
            }}
          >
            {icon}
          </Typography>
          <Typography
            className="label"
            variant="body2"
            noWrap
            color={colors.gray[400]}
            sx={{
              fontSize: { md: "0.75rem", lg: "0.85rem" },
              transition: "color 0.2s ease-in-out",
            }}
          >
            {label}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            flexShrink: 0,
            ml: 1,
          }}
        >
          <Typography
            className="count"
            variant="body2"
            color={colors.gray[400]}
            sx={{
              fontWeight: "bold",
              fontSize: { md: "0.7rem", lg: "0.85rem" },
              whiteSpace: "nowrap",
            }}
          >
            {minutes > 0 && minutes < 60 ? `${Math.round(minutes)}m` : minutes >= 60 ? `${(minutes / 60).toFixed(1)}h` : "0m"}
          </Typography>

          <Typography color={colors.gray[300]} sx={{ fontSize: "0.7rem" }}>
            •
          </Typography>

          <Typography
            className="count"
            variant="body2"
            color={colors.gray[400]}
            sx={{
              fontSize: { md: "0.7rem", lg: "0.85rem" },
              whiteSpace: "nowrap",
            }}
          >
            {count} {count === 1 ? "user" : "users"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ width: "100%", height: 6, bgcolor: colors.gray[100], borderRadius: 4, overflow: "hidden" }}>
        <Box
          className="bar"
          sx={{
            width: `${widthPercent}%`,
            height: "100%",
            bgcolor: color,
            borderRadius: 4,
            opacity: 0.5,
            transition: "width 1s ease-in-out, opacity 0.2s ease-in-out",
          }}
        />
      </Box>
    </Box>
  );
};

export const WorkforceChartNew: React.FC<{ hoursByRole: any }> = ({ hoursByRole }) => {
  const { data, isLoading, isError, error } = useGetTotalWorkforce();

  const counts = useMemo(() => {
    const map = new Map<EWorkforceRole, number>();
    ROLES.forEach((role) => map.set(role, getRoleCount(data, role)));
    return map;
  }, [data]);

  const maxCount = useMemo(() => {
    let max = 0;
    counts.forEach((val) => {
      if (val > max) max = val;
    });
    return max || 1;
  }, [counts]);

  if (isLoading) {
    return (
      <Box sx={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <LoadingCard />
      </Box>
    );
  }

  if (isError) {
    const isForbidden = isBenefitForbiddenError(error);
    if (isForbidden) {
      return (
        <WidgetBenefitBlockedState
          title="Workforce Analytics Locked"
          message={getBenefitErrorMessage(error, "Your current plan does not include workforce analytics.")}
          height="100%"
        />
      );
    }

    return (
      <Box sx={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
        <Typography color="error" variant="body2" align="center" sx={{ fontWeight: 500 }}>
          Failed to load workforce stats
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: 1.5, px: 1, py: 1, justifyContent: "space-evenly" }}>
      {ROLES.map((role) => (
        <RoleBar
          key={role}
          role={role}
          count={counts.get(role) ?? 0}
          minutes={hoursByRole?.totalsByRole?.[role] ?? 0}
          widthPercent={((counts.get(role) ?? 0) / maxCount) * 100}
        />
      ))}
    </Box>
  );
};
